import React, { useState } from "react";
import { Alert, Box } from "@mui/material";
import { palette } from "../../../styles/theme";
import { resolveSpecsImageUrl } from "../specsAssetUrls";
import { canPreviewImageUrl } from "../specsEditorUtils";
import {
  DEFAULT_SPECS_IMAGE_UPLOAD_ACCEPT,
  DEFAULT_SPECS_IMAGE_UPLOAD_VALIDATION,
  uploadSpecsImageFile,
  type SpecsImageUploadContext,
  type SpecsImageUploadValidationOptions,
} from "../specsImageUpload";
import { SpecsAssetImage } from "./SpecsAssetImage";
import { SpecsImageUploadAdornment } from "./SpecsImageUploadAdornment";
import { SpecsImageUrlPreviewButton } from "./SpecsImageUrlPreviewButton";
import { SpecsInputField } from "./SpecsInputField";

export function SpecsImageUrlField({
  value,
  previewSrc,
  previewAlt,
  addLeftPreview = false,
  uploadContext,
  uploadValidation = DEFAULT_SPECS_IMAGE_UPLOAD_VALIDATION,
  accept = DEFAULT_SPECS_IMAGE_UPLOAD_ACCEPT,
  ...props
}: React.ComponentProps<typeof SpecsInputField> & {
  value: string;
  previewSrc?: string;
  previewAlt?: string;
  addLeftPreview?: boolean;
  uploadContext?: SpecsImageUploadContext;
  uploadValidation?: SpecsImageUploadValidationOptions;
  accept?: string;
}) {
  const effectivePreviewSrc = previewSrc || value;
  const resolvedPreviewSrc = resolveSpecsImageUrl(effectivePreviewSrc);
  const previewable = canPreviewImageUrl(resolvedPreviewSrc);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const handleUpload = async (file: File) => {
    setUploading(true);
    setUploadError("");

    try {
      const nextUrl = await uploadSpecsImageFile(
        file,
        uploadContext,
        uploadValidation
      );
      props.onChange?.({
        target: { value: nextUrl },
      } as React.ChangeEvent<HTMLInputElement>);
    } catch (error) {
      setUploadError(
        error instanceof Error ? error.message : "Failed to upload image"
      );
    } finally {
      setUploading(false);
    }
  };

  const field = (
    <SpecsInputField
      {...props}
      value={value}
      // Keep the field open while the native file picker steals focus.
      closeOnBlur={false}
      InputProps={{
        ...props.InputProps,
        endAdornment: (
          <>
            {props.InputProps?.endAdornment}
            <SpecsImageUploadAdornment
              uploading={uploading}
              onUpload={handleUpload}
              accept={accept}
            />
          </>
        ),
      }}
    />
  );

  return (
    <Box>
      {addLeftPreview ? (
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
          <Box sx={{ pb: 0.75, flexShrink: 0 }}>
            <SpecsImageUrlPreviewButton
              src={resolvedPreviewSrc}
              alt={previewAlt ?? "Preview"}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>{field}</Box>
        </Box>
      ) : (
        field
      )}
      {uploadError ? (
        <Alert severity="error" sx={{ mt: 1, py: 0 }}>
          {uploadError}
        </Alert>
      ) : null}
      {previewable && !addLeftPreview && (
        <Box
          sx={{
            mt: 1,
            width: 56,
            height: 56,
            borderRadius: "8px",
            overflow: "hidden",
            border: `1px solid ${palette.grey.lightGrey}`,
            bgcolor: palette.grey.lightBackground,
          }}
        >
          <SpecsAssetImage
            src={resolvedPreviewSrc}
            alt={previewAlt ?? "Preview"}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: "block",
            }}
          />
        </Box>
      )}
    </Box>
  );
}
